// HelpModal - User Manual Modal with Navigation

export class HelpModal {
  private overlayEl: HTMLElement | null = null;

  open(): void {
    if (this.overlayEl) {
      this.overlayEl.classList.remove('hidden');
      return;
    }

    this.overlayEl = document.createElement('div');
    this.overlayEl.className = 'help-modal-overlay';
    this.overlayEl.innerHTML = `
      <div class="help-modal-container">
        <div class="help-modal-sidebar">
          <div class="help-modal-header">
            <h2>User Manual</h2>
          </div>
          <nav class="help-modal-nav">
            <a href="#intro" class="help-nav-link active" data-section="intro">
              <span class="material-symbols-outlined">home</span>
              Introducción
            </a>
            <a href="#concepts" class="help-nav-link" data-section="concepts">
              <span class="material-symbols-outlined">lightbulb</span>
              Conceptos Clave
            </a>
            <a href="#getting-started" class="help-nav-link" data-section="getting-started">
              <span class="material-symbols-outlined">rocket_launch</span>
              Primeros Pasos
            </a>
            <a href="#products" class="help-nav-link" data-section="products">
              <span class="material-symbols-outlined">inventory_2</span>
              Gestión de Productos
            </a>
            <a href="#audiences" class="help-nav-link" data-section="audiences">
              <span class="material-symbols-outlined">group</span>
              Gestión de Audiencias
            </a>
            <a href="#playbooks" class="help-nav-link" data-section="playbooks">
              <span class="material-symbols-outlined">menu_book</span>
              Playbooks Dinámicos
            </a>
            <a href="#content" class="help-nav-link" data-section="content">
              <span class="material-symbols-outlined">auto_awesome</span>
              Generación de Contenido
            </a>
            <a href="#classification" class="help-nav-link" data-section="classification">
              <span class="material-symbols-outlined">category</span>
              Clasificación Automática
            </a>
            <a href="#focus" class="help-nav-link" data-section="focus">
              <span class="material-symbols-outlined">center_focus_strong</span>
              Focus Management
            </a>
            <a href="#use-cases" class="help-nav-link" data-section="use-cases">
              <span class="material-symbols-outlined">psychology</span>
              Casos de Uso
            </a>
            <a href="#faq" class="help-nav-link" data-section="faq">
              <span class="material-symbols-outlined">quiz</span>
              Preguntas Frecuentes
            </a>
            <a href="#troubleshooting" class="help-nav-link" data-section="troubleshooting">
              <span class="material-symbols-outlined">bug_report</span>
              Troubleshooting
            </a>
          </nav>
        </div>

        <div class="help-modal-content-wrapper">
          <div class="help-modal-header-bar">
            <input type="text" id="help-search" class="help-search-input" placeholder="Search manual...">
            <button class="icon-btn" id="help-close">
              <span class="material-symbols-outlined">close</span>
            </button>
          </div>

          <div class="help-modal-content" id="help-content">
            ${this.renderContent()}
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(this.overlayEl);
    this.attachEventListeners();
  }

  close(): void {
    if (this.overlayEl) {
      this.overlayEl.classList.add('hidden');
    }
  }

  private attachEventListeners(): void {
    if (!this.overlayEl) return;

    // Close button
    const closeBtn = this.overlayEl.querySelector('#help-close');
    closeBtn?.addEventListener('click', () => this.close());

    // Click outside to close
    this.overlayEl.addEventListener('click', (e) => {
      if (e.target === this.overlayEl) this.close();
    });

    // ESC to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.overlayEl && !this.overlayEl.classList.contains('hidden')) {
        this.close();
      }
    });

    // Navigation links
    const navLinks = this.overlayEl.querySelectorAll('.help-nav-link');
    navLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const section = (link as HTMLElement).dataset.section;
        if (section) {
          this.scrollToSection(section);

          // Update active state
          navLinks.forEach(l => l.classList.remove('active'));
          link.classList.add('active');
        }
      });
    });

    // Search
    const searchInput = this.overlayEl.querySelector('#help-search') as HTMLInputElement;
    searchInput?.addEventListener('input', () => {
      this.filterContent(searchInput.value);
    });
  }

  private scrollToSection(sectionId: string): void {
    const contentEl = this.overlayEl?.querySelector('#help-content');
    const section = contentEl?.querySelector(`#${sectionId}`);

    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  private filterContent(query: string): void {
    const contentEl = this.overlayEl?.querySelector('#help-content');
    if (!contentEl) return;

    const sections = contentEl.querySelectorAll('.help-section');
    const lowerQuery = query.toLowerCase();

    if (!query.trim()) {
      sections.forEach(section => {
        (section as HTMLElement).style.display = 'block';
      });
      return;
    }

    sections.forEach(section => {
      const text = section.textContent?.toLowerCase() || '';
      (section as HTMLElement).style.display = text.includes(lowerQuery) ? 'block' : 'none';
    });
  }

  private renderContent(): string {
    return `
      <!-- Introducción -->
      <section class="help-section" id="intro">
        <h1>Audience Knowledge System</h1>
        <p class="help-lead">Tu "cerebro de audiencias" - sistema inteligente para crear contenido personalizado.</p>

        <div class="help-card">
          <h3>¿Qué hace este sistema?</h3>
          <ul>
            <li><strong>Entiende a tus audiencias</strong> - Captura pains, objectives, fears, desires</li>
            <li><strong>Clasifica contenido automáticamente</strong> - Detecta qué contenido sirve para qué audiencia</li>
            <li><strong>Genera playbooks estratégicos</strong> - Usa frameworks de Schwartz, Cialdini, Brunson</li>
            <li><strong>Crea contenido personalizado</strong> - Reels, newsletters, LinkedIn DMs adaptados</li>
            <li><strong>Aprende de tu feedback</strong> - Mejora con cada validación</li>
          </ul>
        </div>

        <div class="help-card help-card-success">
          <h4>Resultado</h4>
          <p>En lugar de adivinar qué contenido crear, tienes un sistema que:</p>
          <ul>
            <li>Clasifica cada recurso automáticamente</li>
            <li>Sugiere ángulos basados en frameworks probados</li>
            <li>Genera contenido específico por audiencia</li>
            <li>Todo en minutos, no horas</li>
          </ul>
        </div>
      </section>

      <!-- Conceptos Clave -->
      <section class="help-section" id="concepts">
        <h2>Conceptos Clave</h2>

        <div class="help-card">
          <h3>1. Productos</h3>
          <p>Tus SaaS, servicios, o infoproductos.</p>
          <p><strong>Ejemplo:</strong> Ads Concierge, Messenger Center, Best Ads</p>
          <p><strong>Campos:</strong> Name, Description, Value Proposition, Target Market</p>
        </div>

        <div class="help-card">
          <h3>2. Audiencias</h3>
          <p>Personas específicas dentro de tu target market.</p>
          <p><strong>Ejemplo:</strong> Media Buyers Principiantes, Agency Owners, In-house Marketers</p>
          <p><strong>Componentes clave:</strong></p>
          <ul>
            <li><strong>Pains:</strong> Qué les duele</li>
            <li><strong>Objectives:</strong> Qué quieren lograr</li>
            <li><strong>Fears:</strong> Qué temen</li>
            <li><strong>Desires:</strong> Qué desean profundamente</li>
            <li><strong>Awareness Level:</strong> unaware → problem_aware → solution_aware → product_aware → most_aware</li>
            <li><strong>Funnel Stage:</strong> TOFU (awareness) → MOFU (consideration) → BOFU (decision)</li>
          </ul>
        </div>

        <div class="help-card">
          <h3>3. Playbooks</h3>
          <p><strong>NO son plantillas estáticas.</strong> Son estrategias dinámicas generadas por AI.</p>
          <p><strong>Se basan en:</strong></p>
          <ul>
            <li>Eugene Schwartz (5 Awareness Levels)</li>
            <li>Robert Cialdini (6 Principios de Influencia)</li>
            <li>Russell Brunson (Prime Mover, Value Ladder)</li>
            <li>Direct Response (Hopkins, Halbert, Kennedy)</li>
          </ul>
          <p><strong>Componentes:</strong> Central Message, Angles, Sequence, Enabled Outputs</p>
        </div>

        <div class="help-card">
          <h3>4. Content Outputs</h3>
          <p>Contenido generado desde playbooks.</p>
          <p><strong>Tipos:</strong></p>
          <ul>
            <li><code>reel</code> - Instagram/TikTok Reel (60 seg)</li>
            <li><code>youtube</code> - Video largo (10 min)</li>
            <li><code>newsletter</code> - Email newsletter</li>
            <li><code>linkedin_dm</code> - Mensaje directo LinkedIn</li>
            <li><code>landing_page</code> - Copy de landing page</li>
            <li><code>ad_copy</code> - Copy de ad</li>
            <li><code>thread</code> - Twitter/X thread</li>
          </ul>
        </div>
      </section>

      <!-- Primeros Pasos -->
      <section class="help-section" id="getting-started">
        <h2>Primeros Pasos</h2>

        <div class="help-steps">
          <div class="help-step">
            <div class="help-step-number">1</div>
            <div class="help-step-content">
              <h4>Acceder al Sistema</h4>
              <p>Click en botón <strong>"Audience"</strong> (icono de grupos) en la barra superior</p>
              <p>Verás 3 tabs: Products | Audiences | Playbooks</p>
            </div>
          </div>

          <div class="help-step">
            <div class="help-step-number">2</div>
            <div class="help-step-content">
              <h4>Crear Primer Producto</h4>
              <p>En tab Products, click <strong>"New Product"</strong></p>
              <p>Completa:</p>
              <ul>
                <li>Product name</li>
                <li>Description</li>
                <li>Value proposition</li>
                <li>Target market</li>
              </ul>
            </div>
          </div>

          <div class="help-step">
            <div class="help-step-number">3</div>
            <div class="help-step-content">
              <h4>Crear Primera Audiencia</h4>
              <p>Click <strong>"View Audiences"</strong> en tu product card</p>
              <p>Define pains, objectives, fears, desires</p>
              <p>Selecciona awareness level y funnel stage</p>
            </div>
          </div>

          <div class="help-step">
            <div class="help-step-number">4</div>
            <div class="help-step-content">
              <h4>Generar Playbook</h4>
              <p>Tab Playbooks → Selecciona producto</p>
              <p>Click <strong>"Generate Playbook"</strong></p>
              <p>AI generará estrategia completa (3-5 segundos)</p>
            </div>
          </div>

          <div class="help-step">
            <div class="help-step-number">5</div>
            <div class="help-step-content">
              <h4>Generar Contenido</h4>
              <p>Click <strong>"Generate Content"</strong> en playbook</p>
              <p>Selecciona output type (reel, newsletter, etc.)</p>
              <p>Click Generate → Copy to clipboard → ¡Usa!</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Gestión de Productos -->
      <section class="help-section" id="products">
        <h2>Gestión de Productos</h2>

        <div class="help-card">
          <h3>¿Qué es un Producto?</h3>
          <p>Cada uno de tus SaaS, servicios o infoproductos.</p>
        </div>

        <div class="help-card">
          <h3>Crear Producto</h3>
          <ol>
            <li>Tab <strong>Products</strong></li>
            <li>Click <strong>"New Product"</strong></li>
            <li>Completa prompts uno por uno</li>
            <li>Product card aparecerá automáticamente</li>
          </ol>
        </div>

        <div class="help-card help-card-warning">
          <h4>⚠️ Mejores Prácticas</h4>
          <p><strong>Value Proposition - Sé específico:</strong></p>
          <p>❌ "Ayudamos a hacer ads mejor"</p>
          <p>✅ "Reduce tu CAC en 40% con AI que optimiza bids en tiempo real"</p>

          <p class="mt-2"><strong>Target Market - Incluye números:</strong></p>
          <p>❌ "Marketers"</p>
          <p>✅ "Performance marketers con presupuesto $10k+/mo en Meta ads"</p>
        </div>
      </section>

      <!-- Gestión de Audiencias -->
      <section class="help-section" id="audiences">
        <h2>Gestión de Audiencias</h2>

        <div class="help-card">
          <h3>Estructura de Audiencia</h3>
          <p><strong>Pains (Dolores):</strong> Problemas específicos que enfrentan</p>
          <p class="help-example">Ejemplo: "No saben qué métricas vigilar: ¿CTR? ¿CPC? ¿ROAS?"</p>

          <p class="mt-2"><strong>Objectives (Objetivos):</strong> Qué quieren lograr</p>
          <p class="help-example">Ejemplo: "Reducir CAC de $120 a $50 en 60 días"</p>

          <p class="mt-2"><strong>Fears (Miedos):</strong> Qué temen</p>
          <p class="help-example">Ejemplo: "Ser despedido por quemar $10k sin resultados"</p>

          <p class="mt-2"><strong>Desires (Deseos):</strong> Qué desean profundamente</p>
          <p class="help-example">Ejemplo: "Ser reconocido como el experto de ads en su empresa"</p>
        </div>

        <div class="help-card">
          <h3>Awareness Levels (Schwartz)</h3>
          <table class="help-table">
            <tr>
              <th>Level</th>
              <th>Cuándo usarlo</th>
              <th>Mensaje apropiado</th>
            </tr>
            <tr>
              <td><code>unaware</code></td>
              <td>No saben que tienen problema</td>
              <td>Educar sobre el problema</td>
            </tr>
            <tr>
              <td><code>problem_aware</code></td>
              <td>Saben del problema</td>
              <td>Presentar soluciones</td>
            </tr>
            <tr>
              <td><code>solution_aware</code></td>
              <td>Evalúan opciones</td>
              <td>Diferenciarte</td>
            </tr>
            <tr>
              <td><code>product_aware</code></td>
              <td>Conocen tu producto</td>
              <td>Superar objeciones</td>
            </tr>
            <tr>
              <td><code>most_aware</code></td>
              <td>Ya son clientes</td>
              <td>Upsell, retention</td>
            </tr>
          </table>
        </div>
      </section>

      <!-- Playbooks -->
      <section class="help-section" id="playbooks">
        <h2>Playbooks Dinámicos</h2>

        <div class="help-card help-card-success">
          <h3>¿Qué hace un Playbook?</h3>
          <p>Un playbook NO es una plantilla. Es una <strong>estrategia dinámica</strong> que:</p>
          <ol>
            <li>Analiza tu producto + audiencia</li>
            <li>Aplica frameworks de marketing clásicos</li>
            <li>Genera mensaje central + ángulos estratégicos</li>
            <li>Define secuencia de contenido</li>
            <li>Configura outputs habilitados</li>
            <li>Evoluciona con nuevos datos</li>
          </ol>
        </div>

        <div class="help-card">
          <h3>Generar Playbook</h3>
          <ol>
            <li>Tab <strong>Playbooks</strong></li>
            <li>Selecciona producto en filtro</li>
            <li>Click <strong>"Generate Playbook"</strong></li>
            <li>Opcional: Añade guidance ("Enfócate en testimonios")</li>
            <li>Espera 3-5 segundos</li>
            <li>¡Listo!</li>
          </ol>
        </div>

        <div class="help-card">
          <h3>Evolucionar Playbook</h3>
          <p><strong>¿Cuándo?</strong></p>
          <ul>
            <li>Ingresaste nuevos recursos relevantes</li>
            <li>Tienes métricas de contenido publicado</li>
            <li>La estrategia necesita refinarse</li>
          </ul>
          <p><strong>Cómo:</strong> Click "Evolve" → Selecciona razón → Añade guidance</p>
        </div>
      </section>

      <!-- Generación de Contenido -->
      <section class="help-section" id="content">
        <h2>Generación de Contenido</h2>

        <div class="help-steps">
          <div class="help-step">
            <div class="help-step-number">1</div>
            <div class="help-step-content">
              <h4>Selecciona Playbook</h4>
              <p>En tab Playbooks, encuentra el playbook que quieres usar</p>
            </div>
          </div>

          <div class="help-step">
            <div class="help-step-number">2</div>
            <div class="help-step-content">
              <h4>Click "Generate Content"</h4>
              <p>Modal se abrirá con opciones</p>
            </div>
          </div>

          <div class="help-step">
            <div class="help-step-number">3</div>
            <div class="help-step-content">
              <h4>Configura</h4>
              <ul>
                <li><strong>Output Type:</strong> reel, youtube, newsletter, etc.</li>
                <li><strong>Provider:</strong> Claude Only (GetPoppy requiere API key)</li>
                <li><strong>Guidance:</strong> Opcional, ej: "Usa hook de miedo"</li>
              </ul>
            </div>
          </div>

          <div class="help-step">
            <div class="help-step-number">4</div>
            <div class="help-step-content">
              <h4>Genera y Copia</h4>
              <p>Click Generate → Espera 3-5 seg → Click Copy → ¡Usa!</p>
            </div>
          </div>
        </div>

        <div class="help-card">
          <h3>Tipos de Output</h3>
          <ul>
            <li><strong>Reel:</strong> 60 seg, energético, Hook → Problema → Solución → CTA</li>
            <li><strong>YouTube:</strong> 10 min, educativo, estructura completa</li>
            <li><strong>Newsletter:</strong> Email 400-600 palabras</li>
            <li><strong>LinkedIn DM:</strong> Mensaje personalizado 150-250 chars</li>
            <li><strong>Landing Page:</strong> Copy de página de destino</li>
            <li><strong>Ad Copy:</strong> Texto de anuncio optimizado</li>
            <li><strong>Thread:</strong> Hilo de Twitter/X</li>
          </ul>
        </div>
      </section>

      <!-- Clasificación Automática -->
      <section class="help-section" id="classification">
        <h2>Clasificación Automática</h2>

        <div class="help-card">
          <h3>¿Cómo funciona?</h3>
          <p>Cuando ingestas contenido con <code>prefill=true</code>, el sistema automáticamente:</p>
          <ol>
            <li>Clasifica tipo, tags, categoría</li>
            <li>Detecta propósito (content_idea/learning/both)</li>
            <li>Encuentra audiencias relevantes con scores</li>
            <li>Extrae problemas que resuelve</li>
            <li>Identifica ángulos posibles</li>
            <li>Determina awareness level y funnel stage</li>
          </ol>
          <p class="help-note">⏱️ Proceso asíncrono: ~5 segundos en background</p>
        </div>

        <div class="help-card">
          <h3>Interpretar Scores</h3>
          <table class="help-table">
            <tr>
              <th>Score</th>
              <th>Significado</th>
            </tr>
            <tr>
              <td>0.8 - 1.0</td>
              <td>Altamente relevante, usar para este playbook</td>
            </tr>
            <tr>
              <td>0.6 - 0.79</td>
              <td>Relevante, puede ser útil</td>
            </tr>
            <tr>
              <td>0.4 - 0.59</td>
              <td>Parcialmente relevante</td>
            </tr>
            <tr>
              <td>< 0.4</td>
              <td>Poco relevante</td>
            </tr>
          </table>
        </div>
      </section>

      <!-- Focus Management -->
      <section class="help-section" id="focus">
        <h2>Focus Management (Chrome Extension)</h2>

        <div class="help-card">
          <h3>¿Qué es?</h3>
          <p>Sistema de gestión de foco para redirigirte a contenido educativo cuando pierdes la concentración.</p>
          <p><strong>Problema:</strong> Te distraes navegando por redes sociales o sitios sin valor.</p>
          <p><strong>Solución:</strong> La extensión de Chrome detecta cuando pierdes el foco y te redirige a uno de tus contenidos "read later".</p>
        </div>

        <div class="help-card">
          <h3>Cómo Funciona</h3>
          <div class="help-steps">
            <div class="help-step">
              <span class="step-number">1</span>
              <div class="step-content">
                <h4>Marcas contenido como "read later"</h4>
                <p>En el sistema Knowledge o Multimedia</p>
              </div>
            </div>
            <div class="help-step">
              <span class="step-number">2</span>
              <div class="step-content">
                <h4>Extensión consulta endpoint</h4>
                <p>Cada vez que pierdes foco</p>
              </div>
            </div>
            <div class="help-step">
              <span class="step-number">3</span>
              <div class="step-content">
                <h4>Sistema devuelve 25 items aleatorios</h4>
                <p>De tu lista de "read later"</p>
              </div>
            </div>
            <div class="help-step">
              <span class="step-number">4</span>
              <div class="step-content">
                <h4>Extensión te redirige</h4>
                <p>A uno de ellos aleatoriamente</p>
              </div>
            </div>
            <div class="help-step">
              <span class="step-number">5</span>
              <div class="step-content">
                <h4>Marcas como consumido</h4>
                <p>Cuando terminas de leer/ver</p>
              </div>
            </div>
          </div>
        </div>

        <div class="help-card">
          <h3>API Endpoints</h3>

          <h4>GET /api/focus/read-later</h4>
          <p>Obtiene 25 items aleatorios con intent "read_later".</p>
          <p><strong>Características:</strong></p>
          <ul>
            <li>Combina Knowledge + Multimedia</li>
            <li>Orden aleatorio (Fisher-Yates shuffle)</li>
            <li>Máximo 25 items por request</li>
            <li>Sin autenticación requerida</li>
          </ul>

          <h4>POST /api/focus/mark-consumed</h4>
          <p>Marca un item como consumido (cambia o elimina intent).</p>
          <p><strong>Request:</strong></p>
          <pre><code>{
  "url": "https://youtube.com/watch?v=xyz",
  "newIntent": "archived"  // opcional
}</code></pre>
          <p><strong>Errores:</strong></p>
          <ul>
            <li>404 si URL no existe en el sistema</li>
            <li>400 si falta URL en el request</li>
          </ul>
        </div>

        <div class="help-card">
          <h3>Casos de Uso</h3>

          <h4>Estudiante que procrastina</h4>
          <ul>
            <li>Tiene 50 videos educativos guardados</li>
            <li>Cada vez que abre Instagram → redirigido a video</li>
            <li>Resultado: Consume 3-5 videos/día que antes ignoraba</li>
          </ul>

          <h4>Emprendedor con FOMO</h4>
          <ul>
            <li>Guarda artículos de marketing, growth, product</li>
            <li>Se distrae revisando Twitter constantemente</li>
            <li>Resultado: Lee 2-3 artículos/día, aprende estrategias aplicables</li>
          </ul>

          <h4>Creator con biblioteca masiva</h4>
          <ul>
            <li>200+ recursos guardados de newsletters, podcasts, videos</li>
            <li>Nunca encuentra tiempo para consumirlos</li>
            <li>Resultado: Vacía backlog en 2-3 meses, genera ideas para contenido</li>
          </ul>
        </div>

        <div class="help-card">
          <h3>Integración Chrome Extension</h3>
          <p>Ejemplo de manifest permissions:</p>
          <pre><code>{
  "permissions": [
    "tabs",
    "webNavigation",
    "storage"
  ],
  "host_permissions": [
    "https://notepad-custom.thesocialaudience.workers.dev/*"
  ]
}</code></pre>
        </div>
      </section>

      <!-- Casos de Uso -->
      <section class="help-section" id="use-cases">
        <h2>Casos de Uso</h2>

        <div class="help-card">
          <h3>Caso 1: Lanzamiento de Producto Nuevo</h3>
          <ol>
            <li>Crea el producto</li>
            <li>Define 3 audiencias (TOFU, MOFU, BOFU)</li>
            <li>Genera 3 playbooks (uno por audiencia)</li>
            <li>Genera contenido específico para cada funnel stage</li>
          </ol>
          <p><strong>Resultado:</strong> Estrategia completa de lanzamiento en 2-3 horas</p>
        </div>

        <div class="help-card">
          <h3>Caso 2: Repurposing de Newsletter</h3>
          <ol>
            <li>Ingesta newsletter con <code>prefill=true</code></li>
            <li>Sistema clasifica y detecta audiencias (scores)</li>
            <li>Genera 5+ contenidos diferentes por audiencia</li>
          </ol>
          <p><strong>Resultado:</strong> 1 newsletter → 10+ piezas de contenido</p>
        </div>

        <div class="help-card">
          <h3>Caso 3: Campaña 30 Días</h3>
          <p><strong>Días 1-10:</strong> TOFU (Awareness) - Educación</p>
          <p><strong>Días 11-20:</strong> MOFU (Consideration) - Comparación</p>
          <p><strong>Días 21-30:</strong> BOFU (Decision) - Testimonios</p>
          <p><strong>Resultado:</strong> Campaña completa programada en una sesión</p>
        </div>
      </section>

      <!-- FAQ -->
      <section class="help-section" id="faq">
        <h2>Preguntas Frecuentes</h2>

        <div class="help-faq">
          <details class="help-faq-item">
            <summary>¿Necesito saber prompting para usar esto?</summary>
            <p>No. Los prompts están pre-construidos con frameworks de marketing. Solo defines tu producto/audiencia.</p>
          </details>

          <details class="help-faq-item">
            <summary>¿Cuántas audiencias debo crear por producto?</summary>
            <p>Mínimo 2-3, ideal 4-6. Cubre diferentes awareness levels y segmentos.</p>
          </details>

          <details class="help-faq-item">
            <summary>¿Debo validar cada playbook generado?</summary>
            <p>Sí. AI es buena pero no perfecta. Revisa y ajusta antes de generar contenido.</p>
          </details>

          <details class="help-faq-item">
            <summary>¿Puedo usar el contenido sin editar?</summary>
            <p>Puedes, pero recomendamos revisar. AI genera 80-90% del trabajo, tú das el toque final.</p>
          </details>

          <details class="help-faq-item">
            <summary>¿GetPoppy es mejor que Claude?</summary>
            <p>Depende. GetPoppy está optimizado para viral content (reels, threads). Claude es más flexible. Actualmente GetPoppy requiere API key (no configurado aún).</p>
          </details>

          <details class="help-faq-item">
            <summary>¿Cuánto contenido puedo generar por día?</summary>
            <p>No hay límite técnico. Workers AI tiene límites generosos (100k+ requests/día).</p>
          </details>
        </div>
      </section>

      <!-- Troubleshooting -->
      <section class="help-section" id="troubleshooting">
        <h2>Troubleshooting</h2>

        <div class="help-card help-card-warning">
          <h3>UI No Carga</h3>
          <p><strong>Soluciones:</strong></p>
          <ol>
            <li>Hard refresh: Ctrl+Shift+R (Windows) o Cmd+Shift+R (Mac)</li>
            <li>Clear cache y reload</li>
            <li>Abre DevTools (F12) → Console, revisa errores</li>
          </ol>
        </div>

        <div class="help-card help-card-warning">
          <h3>Generación Tarda Mucho</h3>
          <p><strong>Normal:</strong> 3-5 segundos</p>
          <p><strong>Si pasa de 10 segundos:</strong></p>
          <ul>
            <li>Workers AI bajo carga - reintentar en 1 min</li>
            <li>Ver logs: <code>npx wrangler tail notepad-custom</code></li>
          </ul>
        </div>

        <div class="help-card help-card-warning">
          <h3>Contenido Es Muy Genérico</h3>
          <p><strong>Causas:</strong></p>
          <ul>
            <li>Pains/Desires muy genéricos en audiencia</li>
            <li>Playbook no validado</li>
          </ul>
          <p><strong>Solución:</strong> Mejora tu audiencia con detalles específicos</p>
          <p class="help-example">❌ "No saben hacer ads"<br>✅ "Gastan $2k/mes sin saber si CTR de 2.1% es bueno"</p>
        </div>

        <div class="help-card">
          <h3>GetPoppy Retorna Placeholder</h3>
          <p><strong>Status:</strong> Working as intended</p>
          <p><strong>Razón:</strong> GetPoppy API no configurado (requiere API key)</p>
          <p><strong>Mientras tanto:</strong> Usa <code>provider: "claude"</code></p>
        </div>
      </section>

      <!-- Footer -->
      <div class="help-footer">
        <p>¿Necesitas más ayuda? Consulta la documentación técnica en <code>AUDIENCE_USER_MANUAL.md</code></p>
        <p class="help-note">Versión del sistema: FASE 6 - Content Generation completada</p>
      </div>
    `;
  }
}
